import { Badge } from "@/components/ui/badge";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import cacheKeys from "@/config/cache-keys";
import AppService from "@/services/AppService";
import type { Link as LinkType } from "@/types/link/link";
import type { Group } from "@/types/group/group";
import { useQueryClient } from "@tanstack/react-query";
import { CopyIcon, Link as LinkIcon, TrashIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { toast } from "sonner";
import TableHeadSortButton from "@/components/admin/TableHeadSortButton";

const appService = new AppService();

type SortState = {
	field: "name" | "category" | "groups" | "url";
	direction: "asc" | "desc" | "none";
};

interface LinksTableProps {
	links: LinkType[];
	groups: Group[];
}

export const LinksTable = ({ links, groups }: LinksTableProps) => {
	const { t } = useTranslation();
	const [sortState, setSortState] = useState<SortState>({
		field: "name",
		direction: "asc",
	});

	const SortedLinks = useMemo(() => {
		const sorted = links.sort((a, b) => {
			if (sortState.field === "name") {
				return sortState.direction === "asc"
					? a.name.localeCompare(b.name)
					: b.name.localeCompare(a.name);
			} else if (sortState.field === "url") {
				return sortState.direction === "asc"
					? (a.url?.length || 0) - (b.url?.length || 0)
					: (b.url?.length || 0) - (a.url?.length || 0);
			} else if (sortState.field === "groups") {
				return sortState.direction === "asc"
					? (a.groups?.length || 0) - (b.groups?.length || 0)
					: (b.groups?.length || 0) - (a.groups?.length || 0);
			}
			return 0;
		});
		return sorted;
	}, [sortState, links, URL, groups]);

	return (
		<Table className="table-fixed w-full" aria-label="Lenker">
			<TableHeader>
				<TableRow className="bg-moss-green/5 border-b">
					<TableHead className="py-2 px-4 text-xs w-1/4">
						{t("admin.table.name")}
						<TableHeadSortButton
							label=""
							sortDir={
								sortState.field === "name" ? sortState.direction : "none"
							}
							setSortDir={(dir) =>
								setSortState({ field: "name", direction: dir })
							}
						/>
					</TableHead>
					<TableHead className="py-2 px-4 text-xs w-1/4">
						URL
						<TableHeadSortButton
							label=""
							sortDir={sortState.field === "url" ? sortState.direction : "none"}
							setSortDir={(dir) =>
								setSortState({ field: "url", direction: dir })
							}
						/>
					</TableHead>
					<TableHead className="py-2 px-4 text-xs w-1/4">
						{t("admin.table.group")}
						<TableHeadSortButton
							label=""
							sortDir={
								sortState.field === "groups" ? sortState.direction : "none"
							}
							setSortDir={(dir) =>
								setSortState({ field: "groups", direction: dir })
							}
						/>
					</TableHead>
					<TableHead className="text-xs">{t("admin.table.actions")}</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{SortedLinks.length === 0 && (
					<TableRow>
						<TableCell
							className="text-center text-muted-foreground"
							colSpan={4}
						>
							{t("admin.table.message.no-links")}
						</TableCell>
					</TableRow>
				)}
				{SortedLinks.map((link) => (
					<LinksTableRow link={link} groups={groups} key={link.id} />
				))}
			</TableBody>
		</Table>
	);
};

interface LinksTableRowProps {
	link: LinkType;
	groups: Group[];
}

const LinksTableRow = ({ link, groups }: LinksTableRowProps) => {
	const { t } = useTranslation();
	const { accountId } = useParams();
	const queryClient = useQueryClient();

	const groupsForLink = groups
		.filter((group) => (link.groups ?? []).includes(group.id))
		.map((group) => ({
			id: group.id,
			name: group.name,
		}));

	const deleteLink = async () => {
		if (!accountId) return toast.error("Ingen konto funnet.");
		try {
			const promise = appService.deleteLink(link.id);

			toast.promise(promise, {
				loading: "Sletter lenken...",
				success: "Lenken ble slettet",
				error: "Noe gikk galt under sletting av lenke.",
			});

			await promise;

			queryClient.invalidateQueries({
				queryKey: cacheKeys.SINGLE_ACCOUNT(accountId),
			});
		} catch (error) {
			console.error("Error deleting link:", error);
		}
	};

	return (
		<TableRow data-link-id={link.id}>
			<TableCell className="pl-4 text-xs truncate">
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="truncate block max-w-full">{link.name}</span>
					</TooltipTrigger>
					<TooltipContent side="bottom" className="max-w-[300px] text-xs">
						{link.name}
					</TooltipContent>
				</Tooltip>
			</TableCell>
			<TableCell className="px-4">
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="flex items-center gap-1 text-xs truncate">
							<LinkIcon className="h-3 w-3 shrink-0" />
							<span className="truncate text-xs">
								{link.url || "Ingen URL"}
							</span>
						</span>
					</TooltipTrigger>
					<TooltipContent
						side="bottom"
						className="flex items-center gap-2 max-w-[300px]"
					>
						<span className="text-xs truncate">{link.url || "Ingen URL"}</span>
						{link.url && (
							<Button
								variant="secondary"
								size="sm"
								className="h-7 px-2 text-xs"
								onClick={() => {
									navigator.clipboard
										.writeText(link.url)
										.then(() => toast.success("URL kopiert!"))
										.catch(() => toast.error("Kunne ikke kopiere URL"));
								}}
								aria-label={`Kopier URL for ${link.name}`}
							>
								<CopyIcon className="h-3 w-3" />
							</Button>
						)}
					</TooltipContent>
				</Tooltip>
			</TableCell>
			<TableCell>
				<Tooltip>
					<TooltipTrigger asChild>
						<div className="flex flex-wrap gap-1 truncate">
							{groupsForLink.length > 0 ? (
								<>
									{groupsForLink.slice(0, 1).map((group) => (
										<Badge
											key={group.id}
											variant="secondary"
											className="text-xs py-0.5 px-1"
										>
											{group.name}
										</Badge>
									))}
									{groupsForLink.length > 1 && (
										<Badge variant="outline" className="text-xs py-0.5 px-1">
											+{groupsForLink.length - 1}
										</Badge>
									)}
								</>
							) : (
								<span className="text-xs">N/A</span>
							)}
						</div>
					</TooltipTrigger>
					{groupsForLink.length > 0 && (
						<TooltipContent side="bottom" className="max-w-[200px]">
							<p className="text-xs">Tilknyttede grupper:</p>
							<ul className="list-disc pl-4 text-xs ">
								{groupsForLink.map((group) => (
									<li key={group.id}>{group.name}</li>
								))}
							</ul>
						</TooltipContent>
					)}
				</Tooltip>
			</TableCell>
			<TableCell className="py-2 px-4">
				<div className="flex items-center gap-2">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								size="sm"
								variant="secondary"
								className="h-7 px-2 text-xs"
								onClick={() => link.url && window.open(link.url, "_blank")}
								disabled={!link.url}
								aria-label={`Åpne link ${link.name}`}
							>
								<LinkIcon className="h-3 w-3 mr-1" />
								{t("admin.button.open")}
							</Button>
						</TooltipTrigger>
						<TooltipContent side="bottom">
							{t("admin.table.link-open")} {link.name}
						</TooltipContent>
					</Tooltip>
					{/*<Tooltip>
						<TooltipTrigger asChild>
							<Button
								size="sm"
								variant="destructive"
								className="h-7 px-2 text-xs"
								onClick={deleteLink}
								aria-label={`Slett link ${link.name}`}
							>
								<TrashIcon className="h-3 w-3" />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="bottom">Slett {link.name}</TooltipContent>
					</Tooltip>*/}
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button
								size="sm"
								variant="destructive"
								className="h-7 px-2 text-xs"
								aria-label={`Slett link ${link.name}`}
							>
								<TrashIcon className="h-3 w-3" />
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>
									{t("admin.dialog.alert.link-alert-2")}
								</AlertDialogTitle>
								<AlertDialogDescription>
									{t("admin.dialog.alert.link-alert")} <b>{link.name}</b>?
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>
									{t("admin.button.cancel")}{" "}
								</AlertDialogCancel>
								<AlertDialogAction
									onClick={deleteLink}
									className="bg-destructive hover:bg-destructive/80"
								>
									{t("admin.button.delete")}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			</TableCell>
		</TableRow>
	);
};
